import classnames from 'classnames';
import TagPill from './TagPill';
import Tooltip from './Tooltip';
import { SolidStarIcon, SpotlightCertified, TagIcon } from 'icons';
import { Icon } from 'components/common';

interface LenderBadgeData {
  has_exclusive?: boolean;
  has_promo?: boolean;
  is_certified?: boolean;
  is_featured?: boolean;
  name: string;
  team_tag?: {
    color: string;
    text: string;
  };
}

interface LenderBadgesProps {
  className?: string;
  lender: LenderBadgeData;
  promoClassName?: string;
  tooltipPlacement?: 'left' | 'right';
  useTableLayout?: boolean;
}

const LenderBadges = ({
  className = 'd-flex flex-wrap gap-2',
  lender,
  promoClassName,
  tooltipPlacement = 'left',
  useTableLayout = true,
}: LenderBadgesProps) => {
  const tableLayoutBadgeClassName = classnames({ 'order-1': !useTableLayout });

  if (!lender.is_certified && !lender.is_featured && !lender.has_promo && !lender.has_exclusive && !lender?.team_tag) {
    return null;
  }

  return (
    <div className={classnames(className, { 'mt-4': !useTableLayout })}>
      {lender.is_certified && (
        <div
          className={tableLayoutBadgeClassName}
          data-testid="lender-card-certified-indicator"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
          role="button"
          tabIndex={-1}
        >
          <Tooltip
            className="me-2"
            content={
              <span className="d-block text-left">
                Certified by Lender Spotlight. Product data is verified to be accurate and up to date in partnership
                with {lender.name}.
              </span>
            }
            placement={tooltipPlacement}
          >
            <SpotlightCertified />
          </Tooltip>
        </div>
      )}
      {lender.is_featured && (
        <div className={tableLayoutBadgeClassName} data-testid="lender-card-featured-indicator">
          <TagPill color="yellow" icon={<SolidStarIcon />}>
            Featured
          </TagPill>
        </div>
      )}
      {lender.has_promo && (
        <div className={promoClassName} data-testid="lender-card-promo-indicator">
          <TagPill color="green" icon={<TagIcon className="w-2.5" />}>
            Promo
          </TagPill>
        </div>
      )}
      {lender.has_exclusive && (
        <div className={tableLayoutBadgeClassName} data-testid="lender-card-exclusive-indicator">
          <TagPill>Exclusive Rates</TagPill>
        </div>
      )}
      {lender?.team_tag && (
        <div
          className={classnames('mw-full overflow-hidden', tableLayoutBadgeClassName)}
          data-testid="lender-card-tag-indicator"
          onClick={(event) => event.stopPropagation()}
        >
          <Tooltip
            className="d-flex mw-full overflow-hidden"
            content={<span className="d-block text-left">This tag is managed by your team admin.</span>}
            placement={tooltipPlacement}
          >
            <TagPill color={lender.team_tag.color} icon={<Icon name="buildings" solid />}>
              {lender.team_tag.text}
            </TagPill>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default LenderBadges;
